import React, { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import ChipList from '../components/ChipList';
import { formatDate, formatDateTime } from '../lib/format';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import type { CommunityPartner, PartnerUpdate } from '../lib/types';

// File-upload helper — reuses the hero-slides bucket that already has
// admin-write RLS + public read. Same MIME allow-list as the slides
// uploader (JPG/PNG/WebP/GIF, ≤8MB, server-forced Content-Type).
const ALLOWED_LOGO_TYPES: Record<string, { ext: string; contentType: string }> = {
  'image/jpeg': { ext: 'jpg', contentType: 'image/jpeg' },
  'image/png':  { ext: 'png', contentType: 'image/png'  },
  'image/webp': { ext: 'webp', contentType: 'image/webp' },
  'image/gif':  { ext: 'gif', contentType: 'image/gif'  },
};
const MAX_LOGO_BYTES = 4 * 1024 * 1024; // 4MB is plenty for a partner logo

async function uploadPartnerLogo(file: File): Promise<string> {
  const spec = ALLOWED_LOGO_TYPES[file.type];
  if (!spec) throw new Error(`نوع الملف مش مسموح — JPG/PNG/WebP/GIF فقط`);
  if (file.size > MAX_LOGO_BYTES) throw new Error('حجم اللوجو أكبر من 4MB');
  // Batch 46 / SPECS/006: write under `community/` so the per-prefix
  // storage policy `hero_slides_community_write_*` gates this upload
  // on `has_section_access('community')` — a community-only admin can
  // save partner logos; a home-only admin cannot.
  const path = `community/partner-logo-${crypto.randomUUID()}.${spec.ext}`;
  const { error: upErr } = await supabase.storage.from('hero-slides').upload(path, file, {
    cacheControl: '31536000',
    contentType: spec.contentType,
    upsert: false,
  });
  if (upErr) throw upErr;
  const { data } = supabase.storage.from('hero-slides').getPublicUrl(path);
  return data.publicUrl;
}

// Turn a free-typed multi-line string into an array of trimmed lines
// (and back). Used for the branches textarea — the admin types each
// branch on its own line instead of picking from a fixed list.
const linesToArray = (s: string): string[] =>
  s.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
const arrayToLines = (a: string[]): string => a.join('\n');

type FormState = {
  id: string;
  name: string;
  name_en: string;
  logo_url: string;
  org_intro: string;
  org_intro_en: string;
  about: string;
  about_en: string;
  branches: string[];
  branches_en: string[];
  services: string[];
  services_en: string[];
  offers: string[];
  offers_en: string[];
  active: boolean;
  sort_order: number;
};

const emptyForm: FormState = {
  id: '',
  name: '',
  name_en: '',
  logo_url: '',
  org_intro: '',
  org_intro_en: '',
  about: '',
  about_en: '',
  branches: [],
  branches_en: [],
  services: [],
  services_en: [],
  offers: [],
  offers_en: [],
  active: true,
  sort_order: 0,
};

export default function CommunityPartnersPage() {
  const [partners, setPartners] = useState<CommunityPartner[]>([]);
  const [updates, setUpdates] = useState<Record<string, PartnerUpdate[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FormState | null>(null);
  const [creating, setCreating] = useState(false);
  const [expandedPartner, setExpandedPartner] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement | null>(null);
  const confirm = useConfirm();
  // U-ADM-06 · replace the two window.prompt() calls that opened the
  // add-update flow. Native prompt was unstyled + LTR in an RTL app,
  // and was the single worst call site in the audit.
  const [addUpdateFor, setAddUpdateFor] = useState<string | null>(null);
  const [updateDate, setUpdateDate] = useState<string>('');
  const [updateBody, setUpdateBody] = useState<string>('');
  const [updateBodyEn, setUpdateBodyEn] = useState<string>('');
  const [submittingUpdate, setSubmittingUpdate] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const [p, u] = await Promise.all([
      supabase.from('community_partners').select('*').order('sort_order', { ascending: true }),
      supabase
        .from('community_partner_updates')
        .select('*')
        .order('update_date', { ascending: false }),
    ]);
    if (p.error || u.error) {
      setError((p.error ?? u.error)?.message ?? 'حصل خطأ');
      setLoading(false);
      return;
    }
    setPartners((p.data ?? []) as CommunityPartner[]);
    const byPartner: Record<string, PartnerUpdate[]> = {};
    ((u.data ?? []) as PartnerUpdate[]).forEach((row) => {
      if (!byPartner[row.partner_id]) byPartner[row.partner_id] = [];
      byPartner[row.partner_id].push(row);
    });
    setUpdates(byPartner);
    setLoading(false);
  };

  const openCreate = () => {
    setCreating(true);
    setEditing({ ...emptyForm, sort_order: partners.length });
  };

  const openEdit = (p: CommunityPartner) => {
    setCreating(false);
    setEditing({
      id: p.id,
      name: p.name,
      name_en: (p as unknown as { name_en?: string | null }).name_en ?? '',
      logo_url: p.logo_url ?? '',
      org_intro: p.org_intro,
      org_intro_en: (p as unknown as { org_intro_en?: string | null }).org_intro_en ?? '',
      about: p.about,
      about_en: (p as unknown as { about_en?: string | null }).about_en ?? '',
      branches: p.branches,
      branches_en: (p as unknown as { branches_en?: string[] | null }).branches_en ?? [],
      services: p.services,
      services_en: (p as unknown as { services_en?: string[] | null }).services_en ?? [],
      offers: p.offers,
      offers_en: (p as unknown as { offers_en?: string[] | null }).offers_en ?? [],
      active: p.active,
      sort_order: p.sort_order,
    });
  };

  const closeModal = () => {
    setEditing(null);
    setCreating(false);
  };

  const savePartner = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    // Auto-generate a stable id on create (p<uuid-6>) — the admin
    // never types this. Existing rows keep their id.
    const partnerId = creating
      ? `p${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`
      : editing.id;
    const payload = {
      id: partnerId,
      name: editing.name,
      name_en: editing.name_en.trim() || null,
      logo_url: editing.logo_url.trim() ? editing.logo_url.trim() : null,
      org_intro: editing.org_intro,
      org_intro_en: editing.org_intro_en,
      about: editing.about,
      about_en: editing.about_en,
      branches: editing.branches,
      branches_en: editing.branches_en,
      services: editing.services,
      services_en: editing.services_en,
      offers: editing.offers,
      offers_en: editing.offers_en,
      active: editing.active,
      sort_order: editing.sort_order,
      updated_at: new Date().toISOString(),
    };
    const { error: err } = creating
      ? await supabase.from('community_partners').insert(payload)
      : await supabase.from('community_partners').update(payload).eq('id', editing.id);
    if (err) {
      setError(err.message);
      return;
    }
    closeModal();
    await load();
  };

  const deletePartner = async (id: string) => {
    if (!(await confirm({
      title: 'حذف الشريك؟',
      body: 'ده هيمسح كل التحديثات المرتبطة بيه — مش هيقدر يترجع.',
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    setError(null);
    const { error: err } = await supabase.from('community_partners').delete().eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  };

  // Open the styled "add update" Modal for a partner.
  const openAddUpdate = (partnerId: string) => {
    setAddUpdateFor(partnerId);
    setUpdateDate(new Date().toISOString().slice(0, 10));
    setUpdateBody('');
    setUpdateBodyEn('');
  };

  const submitAddUpdate = async () => {
    if (!addUpdateFor) return;
    if (!updateDate || !updateBody.trim()) return;
    setSubmittingUpdate(true);
    const { error: err } = await supabase
      .from('community_partner_updates')
      .insert({
        partner_id: addUpdateFor,
        update_date: updateDate,
        body: updateBody.trim(),
        body_en: updateBodyEn.trim() || null,
      });
    setSubmittingUpdate(false);
    if (err) {
      setError(err.message);
      return;
    }
    setAddUpdateFor(null);
    await load();
  };

  const deleteUpdate = async (id: string) => {
    if (!(await confirm({
      title: 'حذف التحديث؟',
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    const { error: err } = await supabase
      .from('community_partner_updates')
      .delete()
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  };

  return (
    <>
      <PageHeader
        eyebrow="مجتمع مرسال"
        title="الشركاء"
        subtitle="إضافة شركاء وتحديث بياناتهم وعروضهم"
        actions={
          <button className="btn" onClick={openCreate}>
            + شريك جديد
          </button>
        }
      />

      {error && <div className="error-inline">{error}</div>}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : partners.length === 0 ? (
        <div className="card empty">مفيش شركاء لسه — ابدأ بإضافة شريك جديد.</div>
      ) : (
        partners.map((p) => (
          <div key={p.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>{p.name}</h3>
                  {p.active ? (
                    <span className="badge badge-success">نشط</span>
                  ) : (
                    <span className="badge badge-muted">موقوف</span>
                  )}
                </div>
                <p style={{ color: 'var(--text-secondary)', margin: '6px 0' }}>{p.org_intro}</p>
                <div className="stat-row">
                  <span>{p.offers.length} عرض</span>
                  <span>{p.branches.length} فرع</span>
                  <span>{p.services.length} خدمة</span>
                  <span>{updates[p.id]?.length ?? 0} تحديث</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <button className="btn btn-small btn-secondary" onClick={() => openEdit(p)}>
                  تعديل
                </button>
                <button
                  className="btn btn-small btn-danger"
                  onClick={() => void deletePartner(p.id)}
                >
                  حذف
                </button>
                <button
                  className="btn btn-small btn-secondary"
                  onClick={() =>
                    setExpandedPartner(expandedPartner === p.id ? null : p.id)
                  }
                >
                  {expandedPartner === p.id ? 'إخفاء' : 'التحديثات'}
                </button>
              </div>
            </div>

            {expandedPartner === p.id && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong>التحديثات على البروفايل</strong>
                  <button className="btn btn-small" onClick={() => openAddUpdate(p.id)}>
                    + تحديث
                  </button>
                </div>
                {!updates[p.id] || updates[p.id].length === 0 ? (
                  <div className="empty">مفيش تحديثات لسه.</div>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>التاريخ</th>
                        <th>النص</th>
                        <th>مضاف</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {updates[p.id].map((u) => (
                        <tr key={u.id}>
                          <td>{formatDate(u.update_date)}</td>
                          <td>{u.body}</td>
                          <td>{formatDateTime(u.created_at)}</td>
                          <td>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => void deleteUpdate(u.id)}
                            >
                              حذف
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))
      )}

      {editing && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{creating ? 'شريك جديد' : `تعديل: ${editing.name}`}</h2>
              <button className="btn btn-small btn-secondary" onClick={closeModal}>
                إغلاق
              </button>
            </div>

            <form onSubmit={savePartner}>
              <div className="row">
                <div className="field">
                  <label>اسم الشريك (عربي)</label>
                  <input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label>Partner name (English)</label>
                  <input
                    value={editing.name_en}
                    onChange={(e) => setEditing({ ...editing, name_en: e.target.value })}
                    placeholder="Partner name shown in English mode"
                  />
                </div>
              </div>

              <div className="field">
                <label>لوجو الشريك (اختياري)</label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setUploadingLogo(true);
                    setError(null);
                    try {
                      const url = await uploadPartnerLogo(f);
                      setEditing({ ...editing, logo_url: url });
                    } catch (err) {
                      setError(err instanceof Error ? err.message : String(err));
                    } finally {
                      setUploadingLogo(false);
                      if (logoInputRef.current) logoInputRef.current.value = '';
                    }
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {editing.logo_url ? (
                    <img
                      src={editing.logo_url}
                      alt="logo"
                      style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'contain', background: '#f2f2f2' }}
                    />
                  ) : (
                    <div style={{ width: 72, height: 72, borderRadius: 12, background: '#f2f2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
                      لا يوجد
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? 'جاري الرفع...' : editing.logo_url ? 'تغيير اللوجو' : 'رفع لوجو'}
                  </button>
                  {editing.logo_url && (
                    <button
                      type="button"
                      className="btn btn-small btn-danger"
                      onClick={() => setEditing({ ...editing, logo_url: '' })}
                    >
                      مسح
                    </button>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label>تعريف قصير (عربي)</label>
                  <textarea
                    value={editing.org_intro}
                    rows={2}
                    onChange={(e) => setEditing({ ...editing, org_intro: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Short intro (English)</label>
                  <textarea
                    value={editing.org_intro_en}
                    rows={2}
                    onChange={(e) => setEditing({ ...editing, org_intro_en: e.target.value })}
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label>عن الشريك — تفاصيل (عربي)</label>
                  <textarea
                    value={editing.about}
                    rows={3}
                    onChange={(e) => setEditing({ ...editing, about: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>About the partner (English)</label>
                  <textarea
                    value={editing.about_en}
                    rows={3}
                    onChange={(e) => setEditing({ ...editing, about_en: e.target.value })}
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label>العروض (عربي)</label>
                  <ChipList
                    values={editing.offers}
                    onChange={(offers) => setEditing({ ...editing, offers })}
                    placeholder="اكتب عرض واضغط Enter"
                  />
                </div>
                <div className="field">
                  <label>Offers (English)</label>
                  <ChipList
                    values={editing.offers_en}
                    onChange={(offers_en) => setEditing({ ...editing, offers_en })}
                    placeholder="Type an offer and press Enter"
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label>الفروع (عربي)</label>
                  <textarea
                    rows={5}
                    placeholder="فرع في كل سطر"
                    value={arrayToLines(editing.branches)}
                    onChange={(e) => setEditing({ ...editing, branches: linesToArray(e.target.value) })}
                  />
                </div>
                <div className="field">
                  <label>Branches (English)</label>
                  <textarea
                    rows={5}
                    placeholder="One branch per line"
                    value={arrayToLines(editing.branches_en)}
                    onChange={(e) => setEditing({ ...editing, branches_en: linesToArray(e.target.value) })}
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label>الخدمات (عربي)</label>
                  <ChipList
                    values={editing.services}
                    onChange={(services) => setEditing({ ...editing, services })}
                    placeholder="اكتب خدمة واضغط Enter"
                  />
                </div>
                <div className="field">
                  <label>Services (English)</label>
                  <ChipList
                    values={editing.services_en}
                    onChange={(services_en) => setEditing({ ...editing, services_en })}
                    placeholder="Type a service and press Enter"
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label>ترتيب العرض</label>
                  <input
                    type="number"
                    value={editing.sort_order}
                    onChange={(e) =>
                      setEditing({ ...editing, sort_order: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="field">
                  <label>الحالة</label>
                  <select
                    value={editing.active ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setEditing({ ...editing, active: e.target.value === 'active' })
                    }
                  >
                    <option value="active">نشط (يظهر في التطبيق)</option>
                    <option value="inactive">موقوف (مخفي)</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  إلغاء
                </button>
                <button type="submit" className="btn">
                  {creating ? 'إضافة الشريك' : 'حفظ التغييرات'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/*
        U-ADM-06 · styled "add partner update" modal — retires the two
        window.prompt() calls that opened this flow. Date defaults to
        today; body must be non-empty. Dirty-form guard on the Modal
        prompts before backdrop click / Escape if the body is filled.
      */}
      <Modal
        open={Boolean(addUpdateFor)}
        onClose={() => setAddUpdateFor(null)}
        title="إضافة تحديث للشريك"
        dirty={updateBody.trim().length > 0}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setAddUpdateFor(null)}
              disabled={submittingUpdate}
            >
              إلغاء
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => void submitAddUpdate()}
              disabled={submittingUpdate || !updateDate || !updateBody.trim()}
            >
              {submittingUpdate ? 'جاري الحفظ...' : 'حفظ التحديث'}
            </button>
          </>
        }
      >
        <div className="field">
          <label>تاريخ التحديث</label>
          <input
            type="date"
            value={updateDate}
            onChange={(e) => setUpdateDate(e.target.value)}
            style={{ direction: 'ltr' }}
          />
        </div>
        <div className="row">
          <div className="field">
            <label>نص التحديث (عربي)</label>
            <textarea
              rows={4}
              value={updateBody}
              onChange={(e) => setUpdateBody(e.target.value)}
              placeholder="اكتب التحديث هنا..."
            />
          </div>
          <div className="field">
            <label>Update body (English)</label>
            <textarea
              rows={4}
              value={updateBodyEn}
              onChange={(e) => setUpdateBodyEn(e.target.value)}
              placeholder="Update body shown in English mode"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
